import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

export default function HeroCarousel({ items }: { items: MediaItem[] }) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);

  const go = (next: number) => {
    const total = items.length;
    setIndex(((next % total) + total) % total);
  };

  useEffect(() => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  const current = items[index];
  if (!current) return null;

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 水母影视 在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/30 to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex items-end">
        <div className="w-full px-4 md:px-6 pb-14 md:pb-20 pt-24">
          <div key={current.id} className="max-w-2xl animate-fade-up">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs px-2.5 py-1 rounded-md bg-accent-500 text-background-50 font-medium">
                {current.type}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 backdrop-blur-sm">
                {current.region}
              </span>
              <span className="text-xs px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 backdrop-blur-sm">
                {current.year}
              </span>
              <span className="flex items-center gap-1 text-xs text-background-50">
                <i className="ri-star-fill text-accent-500"></i>
                {current.rating.toFixed(1)}
              </span>
            </div>
            <h2 className="font-heading text-2xl md:text-5xl font-bold text-background-50 tracking-tight">
              {current.title}
            </h2>
            <p className="mt-3 md:mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3 max-w-xl">
              {current.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/15 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line text-lg"></i>
                查看详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一部"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-50/15 backdrop-blur text-background-50 items-center justify-center hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一部"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-50/15 backdrop-blur text-background-50 items-center justify-center hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 right-4 md:right-6 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => go(i)}
            className={`transition-all rounded-full cursor-pointer ${
              i === index ? "w-7 h-2 bg-accent-500" : "w-2 h-2 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}